/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import Dialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import TrailerVideoPlayer from '@unicons-comp/VideoPlayer/VideoPlayer';
import {
  BIG_PLAY_BUTTON_STYLE,
  PLAYER_Z_INDEX,
  PROGRESS_CONTROL_STYLE,
  TIME_DISPLAY_STYLE,
  VOLUME_PANEL_STYLE
} from '@constants/unacademyIcons/style.constants';
import { mediaQuery, Spacings } from '@styles/index';
import { useLessonPlayerStore } from '@stores/lessonPlayerStore';
import ControlBar from '@unicons-comp/VideoPlayer/Controls/ControlBar';
import ControlsFeedback from '@unicons-comp/VideoPlayer/Controls/ControlsFeedback';
import { PLAYER_CONTROLS } from 'types/unacademyIcons/player.types';
import { PLAYER_HOTKEYS } from '@constants/unacademyIcons/icons.constants';
import { handleControls } from '@utils/unacademyIcons/playerHelper';
import Close from '@icon/Close';

interface WatchTrailerProps {
  src: string;
  open: boolean;
  handleClose: () => void;
}

const SlideComp = Slide as any;

const DialogComp = Dialog as any;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <SlideComp direction="up" ref={ref} {...props} />;
});

const StyledButton = styled.button`
  cursor: pointer;
  padding: ${Spacings.SPACING_4B};
  position: fixed;
  background: transparent;
  border: none;
  right: ${Spacings.SPACING_4B};
  top: ${Spacings.SPACING_4B};
`;

const StyledVideoWrapper = styled.div`
  & .video-js {
    ${BIG_PLAY_BUTTON_STYLE}
    & .vjs-control-bar {
      position: absolute;
      z-index: ${PLAYER_Z_INDEX.WRAPPER};
      background: none;
      ${mediaQuery.uptoTablet} {
        z-index: ${PLAYER_Z_INDEX.CONTAINER};
      }
      & .vjs-play-control {
        display: none;
      }
      ${VOLUME_PANEL_STYLE}
      ${TIME_DISPLAY_STYLE}
      
      & .vjs-fullscreen-control {
        display: none;
      }
      ${PROGRESS_CONTROL_STYLE}
    }
  }
`;

const StyledDialog = styled(DialogComp)`
  & .MuiBackdrop-root {
    background: var(--color-i-icon-overlay);
  }
  & .MuiDialog-paperFullWidth {
    background: transparent;
    max-width: 1280px;
    ${mediaQuery.uptoIconDesktop} {
      max-width: 1080px;
    }
  }
`;

const WatchTrailer: React.FC<WatchTrailerProps> = ({
  src,
  open,
  handleClose
}) => {
  const feedbackTimer = useRef(null);

  const {
    setFeedback,
    setFullscreen,
    setControlsIsVisible,
    setPaused,
    setPlayer
  } = useLessonPlayerStore();

  const videoJsOptions = useMemo(
    () => ({
      html5: {
        hls: {
          overrideNative: false
        },
        nativeVideoTracks: true,
        nativeAudioTracks: true,
        nativeTextTracks: true
      },
      autoplay: true,
      responsive: true,
      fill: false,
      fluid: true,
      aspectRatio: '16:9',
      controls: true,
      controlBar: {
        pictureInPictureToggle: false,
        captionsButton: false,
        subsCapsButton: false
      },
      bigPlayButton: true,
      userActions: {
        click: true
      },
      sources: [
        {
          src
        }
      ]
    }),
    [src]
  );

  const handleFeedback = (control: PLAYER_CONTROLS) => {
    if (feedbackTimer.current) {
      clearTimeout(feedbackTimer.current);
      feedbackTimer.current = null;
    }
    setFeedback(true, control);
    feedbackTimer.current = setTimeout(() => setFeedback(false, null), 500);
  };

  const handlePlayerReady = (player) => {
    player.on('ready', () => {
      player.focus();
      setPlayer(player);
    });
    player.on('click', () => {
      setControlsIsVisible(true);
      if (!player.paused()) {
        setPaused(false);
      }
    });

    player.on('pause', () => {
      setPaused(true);
      setControlsIsVisible(true);
    });

    player.on('play', () => {
      setControlsIsVisible(true);
      setPaused(false);
    });

    player.on('userinactive', () => {
      if (!player.paused()) {
        setControlsIsVisible(false);
      }
    });

    player.on('useractive', () => {
      setControlsIsVisible(true);
    });

    player.on('pause', () => {
      setPaused(true);
      handleFeedback(PLAYER_CONTROLS.PAUSE);
    });

    player.on('play', () => {
      setPaused(false);
      handleFeedback(PLAYER_CONTROLS.PLAY);
    });

    player.on('mute', () => {
      handleFeedback(PLAYER_CONTROLS.MUTE);
    });

    player.on('unmute', () => {
      handleFeedback(PLAYER_CONTROLS.VOLUME_UP);
    });

    player.on('enterFullscreen', () => {
      setFullscreen(true);
    });

    player.on('keydown', (hotkey) => {
      const keyCode = hotkey.which || hotkey.keyCode;
      if (keyCode === PLAYER_HOTKEYS.M) {
        handleControls(PLAYER_CONTROLS.MUTE, player);
      }
      if (keyCode === PLAYER_HOTKEYS.F) {
        handleControls(PLAYER_CONTROLS.TRIGGER_FULLSCREEN, player);
      }
      if (keyCode === PLAYER_HOTKEYS.K || keyCode === PLAYER_HOTKEYS.SPACE) {
        handleControls(PLAYER_CONTROLS.PLAY_PAUSE, player);
      }
    });
  };

  return (
    <StyledDialog
      fullScreen={false}
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
    >
      <div>
        <StyledButton onClick={handleClose}>
          <Close
            color="var(--color-i-icon-light-tertiary)"
            size={Spacings.SPACING_6B}
          />
        </StyledButton>
        <div id="icons-controls-wrapper">
          <ControlsFeedback />
          <ControlBar isTrailer />
        </div>
        <StyledVideoWrapper>
          <TrailerVideoPlayer
            options={videoJsOptions}
            onReady={handlePlayerReady}
          />
        </StyledVideoWrapper>
      </div>
    </StyledDialog>
  );
};

WatchTrailer.displayName = 'WatchTrailer';

export default WatchTrailer;
